import { ChangeDetectionStrategy, Component, inject, signal, ViewChild } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { I18NextPipe } from 'angular-i18next';
import { ButtonComponent, CheckboxComponent, ModalComponent, ModalFooterDirective, ToastService } from 'mbs-ui-kit';
import { Debt } from 'mbs-ui-shared@components/components/models/license.model';
import { ComputerModalsFacade } from 'mbs-ui-shared@facades/computer.modals.facade';
import { ComputersFacade } from 'mbs-ui-shared@facades/computers.facade';
import Computer, { AgentState, AgentType } from 'mbs-ui-shared@models/Computer';
import { handleRMMError } from 'mbs-ui-shared@utils/handleError';
import { finalize, take } from 'rxjs';
import { getAgent, getBackupVersion, getConnectVersion, getRMMVersion } from './utils';

@Component({
  selector: 'mbs-deep-instinct-modal',
  templateUrl: './deep-instinct-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [ButtonComponent, ReactiveFormsModule, FormsModule, ModalComponent, I18NextPipe, ModalFooterDirective, CheckboxComponent]
})
export class DeepInstinctModalComponent {
  private facade = inject(ComputersFacade);
  private toastService = inject(ToastService);
  i18nPipe = inject(I18NextPipe);
  private modals = inject(ComputerModalsFacade);

  @ViewChild(ModalComponent, { static: true }) baseModal: ModalComponent;

  public debt: Debt = null;
  public payLinkLoading = false;
  public acceptTerms = false;

  public loading = signal(false);

  isWindows(computer: Computer) {
    return computer.os === 'windows';
  }

  compareVersions(firstVersion: string, secondVersion: string): boolean {
    if (!firstVersion || !secondVersion) {
      return false;
    }

    const firstVersionArray = firstVersion.split('.');
    const secondVersionArray = secondVersion.split('.');

    let isFirstGreatherOrEqualSecond = true;

    if (firstVersionArray.length > secondVersionArray.length) {
      return true;
    }
    if (firstVersionArray.length < secondVersionArray.length) {
      return false;
    }

    for (const [index, version] of firstVersionArray.entries()) {
      if (+version < +secondVersionArray[index]) {
        isFirstGreatherOrEqualSecond = false;
      }
      if (+version > +secondVersionArray[index]) {
        break;
      }
    }

    return isFirstGreatherOrEqualSecond;
  }

  isValidRMM(computer: Computer) {
    const validRMMVersion = '1.3.0.0';
    return (
      this.compareVersions(getRMMVersion(computer), validRMMVersion) &&
      getAgent(computer, AgentType.RMM)?.applicationState === AgentState.Enabled
    );
  }

  isValidBackup(computer: Computer) {
    const validBackupVersion = '6.3.5.22';
    return (
      this.compareVersions(getBackupVersion(computer), validBackupVersion) &&
      getAgent(computer, AgentType.Backup)?.applicationState === AgentState.Enabled
    );
  }

  isValidConnect(computer: Computer) {
    const validConnectVersion = '2.4.1';
    return (
      this.compareVersions(getConnectVersion(computer), validConnectVersion) &&
      getAgent(computer, AgentType.RA)?.applicationState === AgentState.Enabled
    );
  }

  isValidComputer() {
    const computer = this.baseModal.data.computer;

    return computer
      ? this.isWindows(computer) && (this.isValidBackup(computer) || this.isValidConnect(computer) || this.isValidRMM(computer))
      : false;
  }

  confirm() {
    const computer = this.baseModal.data.computer;

    if (!this.isValidComputer()) {
      this.showError();
      return;
    }

    this.loading.set(true);
    this.facade
      .installAgent(computer, AgentType.DeepInst)
      .pipe(
        take(1),
        finalize(() => {
          this.loading.set(false);
        })
      )
      .subscribe({
        next: (result) => {
          if (result.isErrorsInResultList) {
            handleRMMError(result.resultList, this.toastService);
          } else {
            this.modals.openInstallationStartedModal(AgentType.DeepInst, computer);
          }
          this.baseModal.close();
        },
        error: (err) => {
          this.toastService.error(err.error.message);
        }
      });
  }

  showError() {
    this.toastService.error(
      'For correct installation, Backup Agent version 6.3.5.22, or Connect version 2.4.1 or RMM version 1.3 must be installed and be in a running state. OS must be Windows'
    );
  }
}
